import styles from "./StockStampHOC.module.scss";
import { FC, PropsWithChildren } from "react";
import NoData from "@/components/no-data/NoData";
import { classNames } from "@/lib/utils";
import { isNil } from "lodash";

import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import { useAtomValue } from "jotai";
import { textColorState } from "@/lib/store";
import { usExchanges } from "@/lib/vars";

export const withStockStamp = <T,>(Component: FC<T & { overviewPage?: boolean; type?: string }>, requiredField: string, boxTitle?: BoxTitle, boxHeading?: string, width?: string) =>
    function WithStockStamp(props: PropsWithChildren<T & { overviewPage?: boolean; type?: string }>) {
        const textColor = useAtomValue(textColorState);
        const isUSStock = usExchanges.includes(props["row"]?.["stock"]?.["exchange"]);

        const handleNoData = () => {
            switch (boxTitle.title) {
                case "Div (12M FWD)":
                    return "No Dividend Forecast";
                case "CDS":
                    return "no CDS";
                case "Options":
                    return "no Options";
                default:
                    return "No Data Available";
            }
        };

        return (
            <div className={classNames(styles.StockStampHOC, props.overviewPage && styles.overviewPage)}>
                {/* A plain box, not a button: a stamp holds its own interactive parts (chips, links), and
                    a button may not contain them (axe nested-interactive). The stamp's link is on its
                    parent (StockStampLink's card, or the stamps table row). */}
                <div className={styles.stampBody} style={{ width: width ? width : "auto" }}>
                    <Card elevation={0} sx={{ height: "100%", background: "transparent" }} className={styles.stock_stamps_hoc_card}>
                        <CardContent sx={{ height: "100%", p: 0, "&:last-child": { pb: 0 } }}>
                            {(boxHeading || boxTitle) && (
                                <Box sx={boxHeading && boxTitle && props.overviewPage ? { height: "20%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start" } : { height: "10%" }} className={"stamp-header"}>
                                    {props.overviewPage ? (
                                        <>
                                            {
                                                <Typography variant="h3" sx={{ fontWeight: 400, height: "50%", textTransform: "initial", display: props.overviewPage == undefined && "none" }}>
                                                    <>{boxHeading || boxTitle.title}</>
                                                </Typography>
                                            }
                                            {boxTitle && boxHeading && (
                                                <Box sx={{ fontSize: 14 }}>
                                                    {boxTitle && (
                                                        <div style={{ textAlign: boxTitle.align }}>
                                                            {boxTitle.propTitle && <span>{props[boxTitle.propTitle]}&nbsp;</span>}
                                                            {/* The label of the stamp's figure ("% of FF Change 5D"), under the h3 above: not a heading (as h6 it broke heading order). */}
                                                            <Typography className={styles.title} variant="subtitle2" component="p" id="stamp-title" sx={{ fontWeight: 700, textTransform: "initial", color: textColor, textAlign: "center" }}>
                                                                {props.type} {boxTitle.title}
                                                            </Typography>
                                                        </div>
                                                    )}
                                                </Box>
                                            )}
                                        </>
                                    ) : (
                                        boxTitle && (
                                            <Box sx={{ fontSize: 14 }}>
                                                {boxTitle && (
                                                    <div style={{ textAlign: boxTitle.align }}>
                                                        {boxTitle.propTitle && <span>{props[boxTitle.propTitle]}&nbsp;</span>}
                                                        {/* Without a heading above it this is the stamp's own title: h2 under the page's h1 (h6 by default broke heading order). */}
                                                        <Typography className={styles.title} variant="subtitle2" component="h2" id="stamp-title" sx={{ fontWeight: 700, textTransform: "initial", color: textColor, textAlign: "center" }}>
                                                            {props.type} {boxTitle.title}
                                                        </Typography>
                                                    </div>
                                                )}
                                            </Box>
                                        )
                                    )}
                                </Box>
                            )}
                            <Box sx={{ height: boxHeading && boxTitle && props.overviewPage ? "80%" : "90%" }} className={"stamp-content"}>
                                {isUSStock || !(isNil(props[requiredField]) || ["N/A", null, NaN].includes(props[requiredField])) ? <Component {...props} /> : <NoData message={handleNoData} />}
                            </Box>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    };

interface BoxTitle {
    title?: string;
    propTitle?: string;
    align?: "start" | "end" | "left" | "right" | "center" | "justify" | "match-parent";
}
